import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelSwitch from 'src/modules/shared/components/atoms/inputs/label-switch';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { PiInfo } from 'react-icons/pi';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  TCCredential,
  TCCredentialStatus,
  TCCredentialUpdateBodyParams,
} from 'src/api/generated';
import {
  credentialEditFormSchema,
  type CredentialEditFormValues,
} from 'src/modules/settings/validations/credentials.validation';

/**
 * Props for CredentialEditModal component.
 */
export interface CredentialEditModalProps {
  /** Credential being edited, or null when modal should be closed. */
  credential: TCCredential | null;
  /** Called when modal should be closed. */
  onClose: () => void;
  /** Called when form is submitted with a valid payload. */
  onSubmit: (params: {
    credentialId: string;
    body: TCCredentialUpdateBodyParams;
  }) => Promise<void>;
  /** Whether the submit action is in progress. */
  loading: boolean;
}

/**
 * CredentialEditModal component.
 */
export const CredentialEditModal: React.FC<CredentialEditModalProps> = ({
  credential,
  onClose,
  onSubmit,
  loading,
}) => {
  const { t } = useI18n();
  const isOpen = !!credential;

  const defaultValues = useMemo<CredentialEditFormValues>(
    () => ({
      name: credential?.name ?? '',
      status: credential?.status ?? TCCredentialStatus.ACTIVE,
      sharedWithTenant: credential?.sharedWithTenant ?? false,
    }),
    [credential]
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CredentialEditFormValues>({
    resolver: zodResolver(credentialEditFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!credential) return;
    reset(defaultValues);
  }, [credential, defaultValues, reset]);

  const handleFormSubmit = async (values: CredentialEditFormValues) => {
    if (!credential?.credentialId) return;

    await onSubmit({
      credentialId: credential.credentialId,
      body: {
        name: values.name.trim(),
        status: values.status,
        sharedWithTenant: values.sharedWithTenant,
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <Text size="h6" weight="semibold">
          {t('settings.credentials.edit.title')}
        </Text>
      }
      body={
        credential ? (
          <form
            className="w-full space-y-3"
            onSubmit={handleSubmit(handleFormSubmit)}
            id="credential-edit-form"
          >
            <LabelTextInput
              label={t('settings.credentials.form.name')}
              labelPosition="top"
              inputProps={{
                ...register('name'),
                error: errors.name?.message,
              }}
            />

            <Controller
              control={control}
              name="status"
              render={({ field: { value, onChange } }) => (
                <div className="flex items-center justify-end">
                  <LabelSwitch
                    label={
                      value === TCCredentialStatus.ACTIVE
                        ? t('settings.credentials.status.active')
                        : t('settings.credentials.status.disabled')
                    }
                    checked={value === TCCredentialStatus.ACTIVE}
                    onChange={(checked) =>
                      onChange(
                        checked
                          ? TCCredentialStatus.ACTIVE
                          : TCCredentialStatus.DISABLED
                      )
                    }
                    size="sm"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="sharedWithTenant"
              render={({ field: { value, onChange } }) => (
                <div className="flex items-center justify-end">
                  <LabelSwitch
                    label={t('settings.credentials.form.sharedWithUsers')}
                    checked={value}
                    onChange={onChange}
                    size="sm"
                  />
                </div>
              )}
            />

            <Alert
              variant="neutral"
              appearance="soft"
              size="sm"
              icon={<PiInfo className="w-4 h-4" />}
              message={t('settings.credentials.form.sharedInfo')}
            />
          </form>
        ) : null
      }
      footer={
        <div className="flex justify-end gap-3 px-4 py-3">
          <Button
            variant="neutral"
            appearance="ghost"
            onClick={onClose}
            disabled={loading || isSubmitting}
          >
            {t('settings.credentials.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="credential-edit-form"
            loading={loading || isSubmitting}
            disabled={loading || isSubmitting}
          >
            {t('settings.credentials.actions.save')}
          </Button>
        </div>
      }
      dialogClassName="max-w-lg"
    />
  );
};
